import { useCallback, useMemo, useState } from 'react'
import type { ChangeEvent, SetStateAction } from 'react'

import { isEqualMonth, isEqualYear } from '@/utils/date'

import type { Exposure } from '@/types/exposure'

export const useExposureList = (
  mediaExposureList: readonly Exposure[],
  countPerPage: number
) => {
  const [selectedYear, setSelectedYear] = useState('')
  const [selectedMonth, setSelectedMonth] = useState('')
  const [selectedTags, setSelectedTags] = useState<readonly string[]>([])
  const [currentPage, setCurrentPage] = useState(1)

  const filteredMediaList = useMemo(
    () =>
      mediaExposureList.filter(({ mediaType, publishDate }) => {
        const yearFilter = selectedYear
          ? isEqualYear(publishDate, selectedYear)
          : true
        const monthFilter = selectedMonth
          ? isEqualMonth(publishDate, selectedMonth)
          : true
        const tagFilter =
          selectedTags.length > 0 ? selectedTags.includes(mediaType.type) : true

        return yearFilter && monthFilter && tagFilter
      }),

    [mediaExposureList, selectedMonth, selectedTags, selectedYear]
  )

  const displayMediaList = useMemo(() => {
    const start = countPerPage * (currentPage - 1)
    const end = start + countPerPage
    const list = filteredMediaList.slice(
      start,
      filteredMediaList.length < end ? undefined : end
    )

    return list
  }, [currentPage, filteredMediaList, countPerPage])

  const changeSelectedYear = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      setSelectedYear(event.target.value)
      setCurrentPage(1)
    },
    []
  )

  const changeSelectedMonth = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      setSelectedMonth(event.target.value)
      setCurrentPage(1)
    },
    []
  )

  const changeSelectedTag = useCallback((text: string) => {
    setSelectedTags((currentTags) => {
      if (!currentTags) {
        return [text]
      }

      if (currentTags.includes(text)) {
        return currentTags.filter((tag) => tag !== text)
      }

      return [...currentTags, text]
    })
    setCurrentPage(1)
  }, [])

  const transitionPage = useCallback((value: SetStateAction<number>) => {
    setCurrentPage(value)
    window.scrollTo({ top: 0 })
  }, [])

  const handleAllTags = useCallback(() => {
    setSelectedTags([])
    setCurrentPage(1)
  }, [])

  return {
    selectedTags,
    displayMediaList,
    totalCount: filteredMediaList.length,
    currentPage,
    changeSelectedYear,
    changeSelectedMonth,
    changeSelectedTag,
    transitionPage,
    handleAllTags,
  }
}
